import { ReactNode, useMemo } from "react"
import {
  Block,
  InfoCircle,
  LabelMedium,
  ListItem,
  Skeleton,
  Tooltip,
  useStyletron,
} from "@mezo-org/mezo-clay"

import {
  DATABASE_DECIMALS_COUNT,
  DEFAULT_MODAL_Z_INDEX,
  FRIENDLY_DECIMALS_AMOUNT,
} from "#/shared/constants"
import { bigIntToHumanReadableFormat, floatToBigInt } from "#/shared/utils"

type SwapModalReviewItemProps = {
  title: string
  value: string
  symbol?: string
  tooltip?: ReactNode
  showWarning?: boolean
  isLoading?: boolean
  tokenDecimals?: number
}

export default function SwapModalReviewItem({
  title,
  value,
  symbol,
  tooltip,
  showWarning,
  isLoading,
  tokenDecimals,
}: SwapModalReviewItemProps) {
  const [css, theme] = useStyletron()

  const content = useMemo(() => {
    if (!symbol)
      return (
        <LabelMedium className={css({ textAlign: "right" })}>
          {value}
        </LabelMedium>
      )

    const decimals = tokenDecimals ?? DATABASE_DECIMALS_COUNT

    const formattedValue = bigIntToHumanReadableFormat(
      floatToBigInt(value, decimals),
      decimals,
      FRIENDLY_DECIMALS_AMOUNT,
    )

    const showFullValue = Number.isNaN(+formattedValue)

    const baseContent = (
      <LabelMedium
        className={css({
          textAlign: "right",
          color: showWarning
            ? theme.colors.contentNegative
            : theme.colors.black,
        })}
        color={showWarning ? theme.colors.contentNegative : theme.colors.black}
      >
        {formattedValue} {symbol}
      </LabelMedium>
    )

    return showFullValue ? (
      <Tooltip
        content={`${value} ${symbol}`}
        overrides={{
          Body: {
            style: {
              zIndex: DEFAULT_MODAL_Z_INDEX,
            },
          },
        }}
      >
        {baseContent}
      </Tooltip>
    ) : (
      baseContent
    )
  }, [css, theme, showWarning, symbol, value, tokenDecimals])

  return (
    <ListItem
      overrides={{
        Content: {
          style: () => ({
            border: "none",
            minHeight: "42px",
            marginLeft: theme.sizing.scale200,
            paddingRight: theme.sizing.scale200,
          }),
        },
      }}
      endEnhancer={() =>
        isLoading ? (
          <Skeleton
            animation
            width={theme.sizing.scale3200}
            height={theme.sizing.scale700}
          />
        ) : (
          content
        )
      }
    >
      <Block
        className={css({
          display: "flex",
          alignItems: "center",
          gap: theme.sizing.scale300,
        })}
      >
        <LabelMedium color={theme.colors.contentSecondary}>{title}</LabelMedium>
        {tooltip && (
          <Tooltip
            content={tooltip}
            position="right"
            overrides={{
              Body: {
                style: {
                  zIndex: DEFAULT_MODAL_Z_INDEX,
                },
              },
            }}
          >
            <InfoCircle
              color={theme.colors.contentSecondary}
              size={theme.sizing.scale700}
            />
          </Tooltip>
        )}
      </Block>
    </ListItem>
  )
}
